import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation, Outlet } from 'react-router-dom';
import SEO from '../../SEO';
import { useAuth, isFounderUser, isAdminUser, getDashboardPath } from '../../../context/AuthContext';
import { STARTUP_NAV } from './nav';
import { Menu, X, AlertTriangle, Bell } from 'lucide-react';
import portalApi from '../../../lib/portalApi';

export default function StartupLayout() {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [startupStatus, setStartupStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);

  // Authenticate user & verify startup dashboard entitlement (exact pattern as MentorLayout)
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (user && !isFounderUser(user) && !isAdminUser(user)) {
      navigate(getDashboardPath(user));
      return;
    }
    if (user) {
      portalApi
        .getStartupPortalStatus()
        .then((res) => {
          setStartupStatus(res);
        })
        .catch(() => {
          setStartupStatus({ hasStartup: false });
        })
        .finally(() => {
          setStatusLoading(false);
        });
    }
  }, [user, authLoading, navigate]);

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  if (authLoading || statusLoading) {
    return (
      <div className="min-h-screen bg-[#08080A] flex items-center justify-center text-white/50 text-sm">
        Loading Startup Workspace...
      </div>
    );
  }

  if (!user) return null;

  const accessStatus = startupStatus?.dashboardAccess?.status || 'active';
  const isSuspended = accessStatus === 'suspended';

  return (
    <>
      <SEO
        title="Innovace Startup Dashboard"
        description="Innovace Accelerator Startup Workspace: Roadmap, metrics, documents, calendar, and investor readiness."
      />
      <div className="min-h-screen bg-[#08080A] text-[#F2F2F4] flex flex-col md:flex-row antialiased font-sans">
        {/* Mobile Header Bar */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 bg-[#131318] border-b border-[#2C2C34] sticky top-0 z-30">
          <div>
            <span className="text-base font-bold tracking-tight text-[#D42B92]">Innovace</span>
            <div className="text-[11px] text-[#8E8E99]">Startup portal</div>
          </div>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 text-[#8E8E99] hover:text-[#F2F2F4] rounded-lg border border-[#2C2C34]"
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </header>

        {/* Sidebar Overlay for Mobile */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 md:hidden"
            onClick={() => setMobileOpen(false)}
          />
        )}

        {/* Sidebar matching mockup */}
        <aside
          className={`fixed top-0 bottom-0 left-0 w-64 bg-[#08080A] border-r border-[#2C2C34] flex flex-col z-50 transition-transform duration-200 md:translate-x-0 ${
            mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
          }`}
        >
          {/* Brand header matching mockup */}
          <div className="p-6 border-b border-[#2C2C34]/60">
            <div className="flex items-center gap-2">
              <span className="text-xl font-bold tracking-tight text-[#D42B92]">Innovace</span>
            </div>
            <div className="text-xs text-[#8E8E99] mt-0.5 truncate">
              Startup portal
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="flex-1 p-3.5 space-y-1.5 overflow-y-auto">
            {STARTUP_NAV.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center justify-between w-full px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                    isActive
                      ? 'bg-[#321129] text-[#F2F2F4] border border-[#D42B92]/50 shadow-sm shadow-[#D42B92]/10 font-semibold'
                      : 'text-[#8E8E99] hover:bg-white/[0.04] hover:text-[#F2F2F4] border border-transparent'
                  }`
                }
              >
                <div className="flex items-center gap-3">
                  {item.icon && <item.icon className="w-4 h-4 shrink-0 text-[#8E8E99]" />}
                  <span>{item.label}</span>
                </div>
                {item.badge && (
                  <span className="w-5 h-5 rounded-full bg-[#D42B92] text-white text-[10px] font-bold flex items-center justify-center shrink-0 shadow-sm shadow-[#D42B92]/30">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* User profile & Sign Out matching mockup bottom */}
          <div className="p-5 border-t border-[#2C2C34]/60">
            <div className="text-sm font-bold text-[#F2F2F4] truncate">
              {startupStatus?.name || 'Kavadea'}
            </div>
            <button
              onClick={async () => {
                await logout();
                navigate('/login');
              }}
              className="text-xs text-[#8E8E99] hover:text-[#F2F2F4] transition-colors mt-0.5 block"
            >
              Sign out
            </button>
          </div>
        </aside>

        {/* Main Content View matching mockup */}
        <main className="flex-1 md:ml-64 min-h-screen overflow-x-hidden">
          {/* Warning banner if access suspended */}
          {isSuspended && (
            <div className="m-4 p-4 rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 text-sm flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 shrink-0 text-red-400" />
              <div>
                <strong>Dashboard Access Suspended:</strong> {startupStatus?.dashboardAccess?.suspensionReason || 'Please contact program administration.'}
              </div>
            </div>
          )}

          <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
            <Outlet context={{ startupStatus }} />
          </div>
        </main>
      </div>
    </>
  );
}
